import { zodResolver } from "@hookform/resolvers/zod";
import { buildErrorMessage } from "@nimble/react-requests/helpers/error-message";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useForm, UseFormReturn } from "react-hook-form";

import {
  expressInterestFormSchema,
  ExpressInterestFormValues,
} from "@/components/shared/buttons/express-interest-button/express-interest-modal/express-interest-form-schema";
import { ExpressInterestFormView } from "@/components/shared/buttons/express-interest-button/express-interest-modal/express-interest-form-view";
import { getMatchStatusQueryKey } from "@/hooks/data/use-match-status";
import { useTracking } from "@/hooks/use-tracking";
import {
  expressInterest,
  ExpressInterestRequest,
} from "@/lib/api-clients/recommendation-api-client";
import { neverthrowize } from "@/lib/data/call-server-api";
import { User } from "@/lib/types/auth";

export async function submitForm({
  data,
  form,
  schoolNcesId,
  user,
  onSubmitSuccess,
  submitRequest = expressInterest,
}: {
  data: ExpressInterestFormValues;
  form: UseFormReturn<ExpressInterestFormValues>;
  user: User;
  schoolNcesId: string;
  onSubmitSuccess: () => void;
  submitRequest?: typeof expressInterest;
}) {
  const payload: ExpressInterestRequest = {
    school_nces_id: schoolNcesId,
    message: data.message ?? null,
    notification_data: {
      candidate_first_name: user.first_name,
      candidate_last_name: user.last_name,
    },
  };

  const result = neverthrowize([submitRequest(user.id, payload)]);

  await result.match(onSubmitSuccess, (error: Error) => {
    form.setError("root", {
      message: buildErrorMessage(error),
    });
  });
}

export function ExpressInterestFormContainer({
  onCancel,
  onSubmitSuccess,
  schoolNcesId,
  schoolName,
  user,
}: {
  onCancel: () => void;
  onSubmitSuccess: () => void;
  schoolNcesId: string;
  schoolName: string;
  user: User;
}) {
  const queryClient = useQueryClient();
  const { trackExpressInterestSubmitted } = useTracking();
  const form = useForm<ExpressInterestFormValues>({
    resolver: zodResolver(expressInterestFormSchema),
    defaultValues: {
      message: undefined,
    },
  });

  const handleSubmitSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: getMatchStatusQueryKey(user.id, schoolNcesId),
    });
    trackExpressInterestSubmitted({
      schoolNcesId,
      schoolName,
    });
    onSubmitSuccess();
  };

  return (
    <ExpressInterestFormView
      form={form}
      onCancel={onCancel}
      onSubmit={form.handleSubmit((data) =>
        submitForm({
          data,
          schoolNcesId,
          user,
          form,
          onSubmitSuccess: handleSubmitSuccess,
        }),
      )}
    />
  );
}
